import React, { useCallback, useMemo, useReducer } from "react";
import { DefaultButton, PrimaryButton } from "@fluentui/react/lib/Button";
import { Toggle } from "@fluentui/react/lib/Toggle";
import { Spinner, SpinnerSize } from "@fluentui/react/lib/Spinner";
import { MessageBar, MessageBarType } from "@fluentui/react/lib/MessageBar";
import { DetailsList, DetailsListLayoutMode, IColumn, SelectionMode } from "@fluentui/react/lib/DetailsList";
import { Separator } from "@fluentui/react/lib/Separator";
import { saveAs } from "file-saver";

import { TroubleshootingToolProps } from "../../troubleshooting-tools.types";
import { PerformanceLogItem, PerformanceLogsAction, PerformanceLogsState } from "./performance-logs.types";
import { buildCsv } from "./performance-logs.helper";
import { getPerformanceLogs } from "../../../../../api/document/PerformanceLogs";
import { formatMessageDefault, formatMessageWithParamsDefault } from "../../../../../utils/helpers/LanguageHelper";

const DASH = "-";

const initialState: PerformanceLogsState = {
  isLoading: false,
  items: null,
  error: null,
  allUsers: false,
};

function reducer(state: PerformanceLogsState, action: PerformanceLogsAction): PerformanceLogsState {
  switch (action.type) {
    case "TOGGLE_SCOPE":
      return { ...state, allUsers: action.payload };
    case "START":
      return { ...state, isLoading: true, items: null, error: null };
    case "SUCCESS":
      return { ...state, isLoading: false, items: action.payload, error: null };
    case "ERROR":
      return { ...state, isLoading: false, items: null, error: action.payload };
    case "RESET":
      return { ...initialState, allUsers: state.allUsers };
    default:
      return state;
  }
}

function renderScalar(value: unknown): string {
  if (value === null || value === undefined || value === "") return DASH;
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "true" : "false";
  return DASH;
}

export const PerformanceLogs: React.FC<TroubleshootingToolProps> = ({ onError, onSuccess }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const hasResults = state.items !== null;

  const analyzeLogs = useCallback(async () => {
    dispatch({ type: "START" });

    try {
      const items = await getPerformanceLogs(state.allUsers);

      dispatch({ type: "SUCCESS", payload: items });

      onSuccess?.(
        formatMessageWithParamsDefault(
          "cof.troubleshooting.performancelogs.onSuccess.analysis",
          "Analysis complete: {count} log(s)",
          { count: items.length }
        )
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error occurred";
      dispatch({ type: "ERROR", payload: message });

      onError?.(error instanceof Error ? error : new Error(message));
    }
  }, [onError, onSuccess, state.allUsers]);

  const exportToCsv = useCallback(() => {
    if (!hasResults || !state.items) return;

    try {
      const csv = buildCsv(state.items);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");

      saveAs(blob, `performance-logs-${timestamp}.csv`);

      onSuccess?.(
        formatMessageDefault(
          "cof.troubleshooting.performancelogs.onSuccess.export",
          "Data exported successfully to CSV file"
        )
      );
    } catch (error) {
      onError?.(
        error instanceof Error
          ? error
          : new Error(formatMessageDefault("cof.troubleshooting.performancelogs.onError.export", "Export failed"))
      );
    }
  }, [hasResults, onError, onSuccess, state.items]);

  const columns: IColumn[] = useMemo(
    () => [
      {
        key: "id",
        name: formatMessageDefault("cof.troubleshooting.performancelogs.column.id", "_id"),
        fieldName: "id",
        minWidth: 160,
        maxWidth: 220,
        isResizable: true,
        onRender: (item: PerformanceLogItem) => (
          <span style={{ fontFamily: "monospace" }}>{renderScalar(item.id)}</span>
        ),
      },
      {
        key: "documentId",
        name: formatMessageDefault("cof.troubleshooting.performancelogs.column.documentId", "Document ID"),
        fieldName: "documentId",
        minWidth: 100,
        maxWidth: 160,
        isResizable: true,
        onRender: (item: PerformanceLogItem) => <span>{renderScalar(item.documentId)}</span>,
      },
      {
        key: "status",
        name: formatMessageDefault("cof.troubleshooting.performancelogs.column.status", "Status"),
        fieldName: "status",
        minWidth: 90,
        maxWidth: 130,
        isResizable: true,
        onRender: (item: PerformanceLogItem) => <span>{renderScalar(item.status)}</span>,
      },
      {
        key: "startDate",
        name: formatMessageDefault("cof.troubleshooting.performancelogs.column.startDate", "Start Date"),
        fieldName: "startDate",
        minWidth: 140,
        maxWidth: 200,
        isResizable: true,
        onRender: (item: PerformanceLogItem) => <span>{renderScalar(item.startDate)}</span>,
      },
      {
        key: "endDate",
        name: formatMessageDefault("cof.troubleshooting.performancelogs.column.endDate", "End Date"),
        fieldName: "endDate",
        minWidth: 140,
        maxWidth: 200,
        isResizable: true,
        onRender: (item: PerformanceLogItem) => <span>{renderScalar(item.endDate)}</span>,
      },
      {
        key: "elaborationType",
        name: formatMessageDefault("cof.troubleshooting.performancelogs.column.elaborationType", "Elaboration Type"),
        fieldName: "elaborationType",
        minWidth: 130,
        maxWidth: 200,
        isResizable: true,
        onRender: (item: PerformanceLogItem) => <span>{renderScalar(item.elaborationType)}</span>,
      },
      {
        key: "objectId",
        name: formatMessageDefault("cof.troubleshooting.performancelogs.column.objectId", "Object ID"),
        fieldName: "objectId",
        minWidth: 120,
        maxWidth: 200,
        isResizable: true,
        onRender: (item: PerformanceLogItem) => <span>{renderScalar(item.objectId)}</span>,
      },
      {
        key: "objectType",
        name: formatMessageDefault("cof.troubleshooting.performancelogs.column.objectType", "Object Type"),
        fieldName: "objectType",
        minWidth: 100,
        maxWidth: 160,
        isResizable: true,
        onRender: (item: PerformanceLogItem) => <span>{renderScalar(item.objectType)}</span>,
      },
    ],
    []
  );

  const renderResults = () => {
    if (!state.items) return null;

    return (
      <div style={{ marginBottom: "1.5rem" }}>
        <Separator alignContent="start">
          <div style={{ fontWeight: 600, fontSize: 16 }}>
            {formatMessageDefault("cof.troubleshooting.performancelogs.results.title", "Performance Logs")} (
            {state.items.length})
          </div>
        </Separator>

        {state.items.length === 0 ? (
          <MessageBar messageBarType={MessageBarType.info} styles={{ root: { marginTop: "0.5rem" } }}>
            {formatMessageDefault("cof.troubleshooting.performancelogs.results.none", "No performance logs found.")}
          </MessageBar>
        ) : (
          <div style={{ marginTop: "0.5rem" }}>
            <DetailsList
              items={state.items}
              columns={columns}
              selectionMode={SelectionMode.none}
              layoutMode={DetailsListLayoutMode.justified}
              isHeaderVisible
              styles={{
                root: {
                  border: "1px solid var(--color-lightblue)",
                  borderRadius: 4,
                },
              }}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: "1rem" }}>
      <div style={{ marginBottom: "1rem" }}>
        <p style={{ margin: "0.5rem 0", color: "var(--color-black)" }}>
          {formatMessageDefault(
            "cof.troubleshooting.performancelogs.description",
            "This tool retrieves and exports the elaboration performance logs (timing, status and metadata of each object elaboration). Use the toggle to include all users, then Export to download the full dataset as CSV."
          )}
        </p>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
        <Toggle
          label={formatMessageDefault("cof.troubleshooting.performancelogs.toggle.label", "Log scope")}
          inlineLabel
          checked={state.allUsers}
          disabled={state.isLoading}
          onText={formatMessageDefault("cof.troubleshooting.performancelogs.toggle.allUsers", "All users")}
          offText={formatMessageDefault("cof.troubleshooting.performancelogs.toggle.currentUser", "Current user only")}
          onChange={(_ev, checked) => dispatch({ type: "TOGGLE_SCOPE", payload: Boolean(checked) })}
          styles={{ root: { marginBottom: 0 } }}
        />

        <PrimaryButton
          text={formatMessageDefault("cof.troubleshooting.performancelogs.button.analyze", "Analyze Logs")}
          onClick={analyzeLogs}
          disabled={state.isLoading}
          iconProps={{ iconName: "Search" }}
        />

        {hasResults && (
          <DefaultButton
            text={formatMessageDefault("cof.troubleshooting.performancelogs.button.export", "Export")}
            onClick={exportToCsv}
            disabled={state.isLoading}
            iconProps={{ iconName: "Download" }}
          />
        )}
      </div>

      {state.isLoading && (
        <div style={{ padding: "2rem 0", textAlign: "center" }}>
          <Spinner
            size={SpinnerSize.large}
            label={formatMessageDefault(
              "cof.troubleshooting.performancelogs.spinner.analyzing",
              "Analyzing performance logs..."
            )}
          />
        </div>
      )}

      {state.error && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline>
          <strong>{formatMessageDefault("cof.troubleshooting.performancelogs.error.prefix", "Error:")}</strong>{" "}
          {state.error}
        </MessageBar>
      )}

      {!state.isLoading && !state.error && hasResults && <div>{renderResults()}</div>}
    </div>
  );
};
